// Picker de fondo REDUCIDO del modo "Fotos de producto".
//
// El `FondoPicker` completo (biblioteca + guardados + subida + automático)
// es un sheet de 2.000 líneas pensado para escenas con modelo. Acá el modo
// ofrece solo tres caminos, así que van como tres cards inline:
//
//   • Fondo blanco          → background_mode 'blanco' + background_id 5
//   • Describir escena      → background_mode 'descripcion' + background_text
//   • Copiar imagen de producto → TODAVÍA NO IMPLEMENTADO (card deshabilitada
//     con badge "Próximamente"). Se muestra a propósito: marca hacia dónde va
//     el modo. Ver ADR-2026-08-13-modo-fotos-producto.
//
// El tipo de selección es el `FondoSelection` compartido con el resto de V2
// (restringido a sus dos variantes soportadas), así el armado del payload en
// InterfazV2 es el MISMO helper que usan design-modelo y el catálogo.

import React, { useEffect, useRef } from 'react';
import { Check, Copy, Type as TypeIcon } from 'lucide-react';
import type { FondoSelection } from '../FondoPicker';
import { PromptRecientesButton } from './PromptRecientesButton';

/** Subconjunto de `FondoSelection` que soporta este modo. */
export type FondoProductoSelection =
  | { mode: 'blanco' }
  | { mode: 'descripcion'; text: string };

/** Type guard para estrechar un `FondoSelection` genérico (el state de
 *  InterfazV2 es compartido con los otros modos) al subconjunto de acá. */
export const esFondoProducto = (
  sel: FondoSelection | null,
): sel is FondoProductoSelection =>
  sel?.mode === 'blanco' || sel?.mode === 'descripcion';

export interface FondoProductoPickerProps {
  selection: FondoProductoSelection;
  onChange: (sel: FondoProductoSelection) => void;
  /** Nombres de los productos elegidos — alimentan el placeholder del
   *  textarea con un ejemplo concreto ("Sobre una mesa de mármol…"). */
  productNames?: string[];
}

export const FondoProductoPicker: React.FC<FondoProductoPickerProps> = ({
  selection,
  onChange,
  productNames = [],
}) => {
  const esBlanco = selection.mode === 'blanco';
  const esDescripcion = selection.mode === 'descripcion';
  const texto = selection.mode === 'descripcion' ? selection.text : '';

  // Auto-focus del textarea al elegir "Describir escena": la card y el campo
  // son un solo gesto, sin este foco había que tocar dos veces.
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const wasDescripcion = useRef(esDescripcion);
  useEffect(() => {
    if (esDescripcion && !wasDescripcion.current) textareaRef.current?.focus();
    wasDescripcion.current = esDescripcion;
  }, [esDescripcion]);

  const placeholder = productNames[0]
    ? `Ej: ${productNames[0]} sobre una mesa de mármol, con luz natural entrando de costado.`
    : 'Ej: sobre una mesa de mármol, con luz natural entrando de costado.';

  return (
    <div className="flex flex-col gap-2.5">
      <div className="grid grid-cols-3 gap-2.5">
        {/* Fondo blanco */}
        <button
          type="button"
          onClick={() => onChange({ mode: 'blanco' })}
          aria-pressed={esBlanco}
          className={`relative h-24 rounded-2xl bg-white border-2 text-left transition-colors active:scale-[0.98] ${
            esBlanco
              ? 'border-brand-primary'
              : 'border-neutral-200 hover:border-[#1644EB]/40'
          }`}
        >
          <div className="h-full flex flex-col items-start justify-between p-3">
            <span className="inline-flex w-9 h-9 rounded-md border-2 border-neutral-300 bg-white" />
            <span className="text-[14px] font-semibold text-brand-black/85 text-left leading-tight">
              Fondo blanco
            </span>
          </div>
          {esBlanco && (
            <span className="absolute top-2 right-2 w-6 h-6 rounded-full inline-flex items-center justify-center bg-brand-primary text-white shadow-md">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </span>
          )}
        </button>

        {/* Describir escena */}
        <button
          type="button"
          onClick={() => onChange({ mode: 'descripcion', text: texto })}
          aria-pressed={esDescripcion}
          className={`relative h-24 rounded-2xl bg-white border-2 text-left transition-colors active:scale-[0.98] ${
            esDescripcion
              ? 'border-brand-primary'
              : 'border-neutral-200 hover:border-[#1644EB]/40'
          }`}
        >
          <div className="h-full flex flex-col items-start justify-between p-3">
            <span className="w-9 h-9 rounded-full bg-[#1644EB]/10 inline-flex items-center justify-center">
              <TypeIcon className="w-4 h-4 text-brand-primary" />
            </span>
            <span className="text-[14px] font-semibold text-brand-black/85 text-left leading-tight">
              Describir escena
            </span>
          </div>
          {esDescripcion && (
            <span className="absolute top-2 right-2 w-6 h-6 rounded-full inline-flex items-center justify-center bg-brand-primary text-white shadow-md">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </span>
          )}
        </button>

        {/* Copiar imagen de producto — pendiente de implementar. */}
        <div
          aria-disabled="true"
          title="Próximamente"
          className="relative h-24 rounded-2xl bg-neutral-50 border-2 border-dashed border-neutral-200 text-left cursor-not-allowed"
        >
          <div className="h-full flex flex-col items-start justify-between p-3 opacity-60">
            <span className="w-9 h-9 rounded-full bg-neutral-200/70 inline-flex items-center justify-center">
              <Copy className="w-4 h-4 text-neutral-500" />
            </span>
            <span className="text-[14px] font-semibold text-brand-black/70 text-left leading-tight">
              Copiar imagen de producto
            </span>
          </div>
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-neutral-200 text-[10.5px] font-semibold text-brand-black/60 uppercase tracking-wide">
            Pronto
          </span>
        </div>
      </div>

      {/* Textarea de la escena — solo con "Describir escena" activa. */}
      {esDescripcion && (
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={texto}
            onChange={(e) =>
              onChange({ mode: 'descripcion', text: e.target.value })
            }
            placeholder={placeholder}
            rows={3}
            className="w-full resize-none px-4 pt-3 pb-11 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
          />
          <PromptRecientesButton
            tipo="escena"
            onPick={(t) => onChange({ mode: 'descripcion', text: t })}
            titulo="Escenas usadas antes"
            className="absolute bottom-2.5 right-2.5"
          />
        </div>
      )}
    </div>
  );
};
